import FieldHint from "./FieldHint";
import type { TeamPlayConfig, TeamPlayLineKind } from "../lib/api";
import {
  LINES_MAX,
  LINEUP_MODE_LABELS,
  LINE_KIND_LABELS,
  TEAM_SIZE_MAX,
  TEAM_SIZE_MIN,
  defaultTeamPlay,
  majorityWins,
  teamPlayEnabled,
  teamPlaySummary,
  teamPlayValidationError,
  normalizeTeamPlayBlock,
} from "../lib/teamPlay";

/** Sección "Por equipos" del Paso 5 Formato (Round Robin y Eliminatoria).
 *
 * Motor de series por equipos (Mini Copa 2026, Eduardo 2026-09-11/12): la
 * categoría se sigue armando con grupos o bracket, pero cada enfrentamiento
 * es una serie de líneas entre dos equipos. Los criterios de desempate no
 * viven aquí: son los de la sección de tabla de posiciones, aplicados sobre
 * los partidos de las series. */

type Props = {
  value: TeamPlayConfig | null | undefined;
  onChange: (next: TeamPlayConfig | null) => void;
  disabled?: boolean;
};

const LINE_KIND_OPTIONS: TeamPlayLineKind[] = ["singles", "doubles", "mixed"];

export default function TeamPlayFormatSection({ value, onChange, disabled = false }: Props) {
  const enabled = teamPlayEnabled(value);
  // Bloque siempre completo: un team_play parcial (sin lines) no debe tirar el Paso 5.
  const block: TeamPlayConfig = enabled ? normalizeTeamPlayBlock(value) : { ...defaultTeamPlay(), enabled: false };
  const validationError = teamPlayValidationError(block);

  function patch(next: Partial<TeamPlayConfig>) {
    onChange({ ...block, enabled: true, ...next });
  }

  function setLines(lines: TeamPlayConfig["lines"], previousCount: number) {
    // Si el admin no había tocado las victorias, siguen la mayoría simple.
    const wins = block.wins_to_take_tie === majorityWins(previousCount)
      ? majorityWins(lines.length)
      : Math.min(block.wins_to_take_tie, Math.max(1, lines.length));
    patch({ lines, wins_to_take_tie: wins });
  }

  return (
    <div className="format-form-section form__full team-play-section">
      <div className="format-form-section__header">
        <h3 className="hint-label">
          Por equipos
          <FieldHint label="¿Cómo funciona una categoría por equipos?">
            Los grupos o el bracket se arman igual, pero cada enfrentamiento es una serie entre dos equipos
            con varias líneas (singles y dobles). Gana la serie el equipo que llega primero a las victorias
            indicadas; la tabla cuenta series ganadas y los desempates son los mismos criterios de la tabla de
            posiciones, calculados sobre los partidos de las series. Mini Copa: equipos de 2, dos singles y un
            dobles, gana con 2.
          </FieldHint>
        </h3>
        {enabled ? <span className="format-scoring-pill">{teamPlaySummary(block)}</span> : null}
      </div>
      <label className="form__checkbox form__checkbox--boxed">
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked ? { ...defaultTeamPlay(), ...block, enabled: true } : null)}
        />
        <span>Esta categoría se juega por equipos (series con líneas)</span>
      </label>

      {enabled ? (
        <>
          <div className="format-form-section__grid">
            <label>
              Jugadores por equipo
              <input
                type="number"
                min={TEAM_SIZE_MIN}
                max={TEAM_SIZE_MAX}
                value={block.team_size}
                disabled={disabled}
                onChange={(event) => patch({ team_size: Number(event.target.value) })}
              />
            </label>
            <label>
              Victorias para ganar la serie
              <input
                type="number"
                min={1}
                max={Math.max(1, block.lines.length)}
                value={block.wins_to_take_tie}
                disabled={disabled}
                onChange={(event) => patch({ wins_to_take_tie: Number(event.target.value) })}
              />
            </label>
            <label>
              Alineación de las líneas
              <select
                value={block.lineup_mode}
                disabled={disabled}
                onChange={(event) => patch({ lineup_mode: event.target.value as TeamPlayConfig["lineup_mode"] })}
              >
                {(Object.keys(LINEUP_MODE_LABELS) as TeamPlayConfig["lineup_mode"][]).map((mode) => (
                  <option key={mode} value={mode}>{LINEUP_MODE_LABELS[mode]}</option>
                ))}
              </select>
            </label>
            <label>
              Marcador por default
              <input
                type="text"
                value={block.default_score}
                disabled={disabled}
                placeholder="6-2 6-2"
                onChange={(event) => patch({ default_score: event.target.value })}
              />
            </label>
          </div>

          <div className="team-play-lines">
            <span className="team-play-lines__title">Líneas de la serie</span>
            <ol className="team-play-lines__list">
              {block.lines.map((line, index) => (
                <li key={`${index}-${line.kind}`} className="team-play-lines__item">
                  <span className="team-play-lines__number">{index + 1}</span>
                  <select
                    aria-label={`Tipo de la línea ${index + 1}`}
                    value={line.kind}
                    disabled={disabled}
                    onChange={(event) => {
                      const lines = block.lines.map((entry, i) => (
                        i === index ? { ...entry, kind: event.target.value as TeamPlayLineKind } : entry
                      ));
                      patch({ lines });
                    }}
                  >
                    {LINE_KIND_OPTIONS.map((kind) => (
                      <option key={kind} value={kind}>{LINE_KIND_LABELS[kind]}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn--ghost btn--compact btn--tap44"
                    disabled={disabled || block.lines.length <= 1}
                    aria-label={`Quitar la línea ${index + 1}`}
                    onClick={() => setLines(block.lines.filter((_, i) => i !== index), block.lines.length)}
                  >
                    Quitar
                  </button>
                </li>
              ))}
            </ol>
            <button
              type="button"
              className="btn btn--ghost btn--compact"
              disabled={disabled || block.lines.length >= LINES_MAX}
              onClick={() => setLines([...block.lines, { kind: "singles" }], block.lines.length)}
            >
              + Agregar línea
            </button>
          </div>

          <label className="form__checkbox form__checkbox--boxed">
            <input
              type="checkbox"
              checked={block.default_counts_as_played}
              disabled={disabled}
              onChange={(event) => patch({ default_counts_as_played: event.target.checked })}
            />
            <span>
              Un partido ganado por default cuenta como jugado con el marcador por default (entra en los
              porcentajes de sets y juegos de los desempates)
            </span>
          </label>

          {validationError ? <p className="form-error">{validationError}</p> : null}
        </>
      ) : null}
    </div>
  );
}
